import { Formik } from 'formik';
import { memo, ReactElement, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { default as css } from './SearchForm.module.scss';
import { nameof } from '../../../../helpers/nameof';
import { isElectron } from '../../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { NavigateFunction, useNavigate, useLocation, Location } from 'react-router-dom';
import { AccountType, DMSType } from '../../../../API/Enums';
import { IAppointment, AppointmentFilter, appointmentSearch } from '../../../../API/GoMoto';
import { GoMotoComponent, GoMotoFunction } from '../../../../API/GoMoto.types';
import { useEnsureAppointment, useAppointmentDispatch, useTypedInFields, AppointmentDispatchTypes } from '../../../../context/Appointment/appointment';
import { useKeyboardDispatch } from '../../../../context/Keyboard/keyboard';
import { KeyboardDispatchTypes } from '../../../../context/Keyboard/keyboard.types';
import { useKioskState, useFullReset, useKioskTimeTable } from '../../../../context/Kiosk/kiosk';
import { useLocalization, useLocalizationExtension } from '../../../../context/Localization/localization';
import { LocalizationType, Locales } from '../../../../context/Localization/localization.types';
import { CTAButton, GMGenericButton } from '../../../../controls/ButtonBase';
import { GMCapitalize } from '../../../../controls/Case';
import { GMDialogError } from '../../../../controls/Dialog';
import { GMForm, GMFormWrapper } from '../../../../controls/Form';
import { Heading } from '../../../../controls/Heading/Heading';
import { GMSizedIcon, GMRoundedIcon } from '../../../../controls/Icon';
import { IconTypes } from '../../../../controls/Icon/GenericIcon/GenericIcon.types';
import { GMRadioGroup, GMRadio } from '../../../../controls/Radio';
import { ScrollTo } from '../../../../controls/ScrollTo/ScrollTo/ScrollTo';
import { ISearchListResultProps, SearchListResult } from '../../../../controls/SearchListResults/SearchListResult/SearchListResult';
import { SearchListResults } from '../../../../controls/SearchListResults/SearchListResults';
import { KeyboardLayouts } from '../../../../controls/SimpleKeyboard/Layout';
import { Spinner } from '../../../../controls/Spinner/Spinner';
import { GMFormTextField, GMFormPhoneField, GMFormPhoneOrEmailField, GMFormEmailField } from '../../../../controls/TextField';
import { useFormatAppointmentDate, getFullDisplayName } from '../../../../helpers/Appointment';
import { useDisplayLocalizedVehicle } from '../../../../helpers/displayVehicle';
import { useNotListedReducer } from '../../../../helpers/notListedReducer';
import { formatAsPhoneNumber } from '../../../../helpers/Number';
import { PathName, AppRoutePaths, AppointmentRoutePaths } from '../../../../helpers/Routes';
import { AppointmentLookupMethod } from '../Lookup';
import { SearchFormProps, SearchFormType } from './SearchForm.types';

/**
 * The form used to search for an appointment that already exists on either kiosk or mobile.
 *
 * @param onAppointmentSelected - {@link SearchFormProps.onAppointmentSelected onAppointmentSelected} description.
 * @param onNotListed - {@link SearchFormProps.onNotListed onNotListed} description.
 * @param lookupMethod - {@link SearchFormProps.lookupMethod lookupMethod} description.
 * @param initialEmail - {@link SearchFormProps.initialEmail initialEmail} description.
 */
export const SearchForm: GoMotoComponent<SearchFormProps> = memo(({
    onAppointmentSelected,
    onNotListed,
    lookupMethod,
    initialEmail,
    initialPhone,
}) => {
    const {
        dealer: { mobileCheckInEnabled, newAppointmentEnabled = true, dmsType },
    } = useKioskState();
    const Localization: LocalizationType = useLocalization();
    const { key, dealer: { key: dealerKey } } = useKioskState();
    const { formatInvalid } = useLocalizationExtension();
    const { start: startNotListed, complete: notListedComplete, inProgress: notListedInProgess } = useNotListedReducer(useEnsureAppointment().appointment);
    const appointmentDispatch = useAppointmentDispatch();
    const formatAppointmentDate = useFormatAppointmentDate();
    const typedInFields = useTypedInFields();
    const navigate: NavigateFunction = useNavigate();
    const location: Location<unknown> = useLocation();
    const reset: VoidFunction = useFullReset();
    const displayLocalizedVehicle = useDisplayLocalizedVehicle();
    const keyboardDispatch = useKeyboardDispatch();
    const { computeNextAvailableCheckinTime, getIsAppointmentTimeAfterOffset } = useKioskTimeTable();

    const [appointments, setAppointmentResults] = useState<IAppointment[]>([]);
    const [seeServiceAdvisor, setSeeServiceAdvisor] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    // We would use submitCount, but that is incremented regardless of form validation.
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

    const isKiosk: boolean = isElectron();

    useEffect(() => {
        if (notListedComplete) {
            onNotListed();
        }
    }, [onNotListed, notListedComplete]);

    const initialValues: SearchFormType = {
        accountType: lookupMethod === AppointmentLookupMethod.Business ? AccountType.business : AccountType.individual,
        lookupMethod: lookupMethod,
        firstName: '',
        lastName: '',
        businessName: '',
        phone: initialPhone ?? '',
        email: initialEmail ?? '',
        phoneOrEmail: typedInFields?.phoneNumber ?? typedInFields?.email ?? '',
        selectedAppointment: undefined as IAppointment | undefined,
        notListed: false,
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async ({ selectedAppointment, notListed, accountType, lookupMethod, ...filter }) => {
                switch (accountType) {
                    case AccountType.business:
                        filter.firstName = '';
                        filter.lastName = '';
                        break;
                    case AccountType.individual:
                        filter.businessName = '';
                        break;
                }

                filter.email = filter.email.trim();

                if (selectedAppointment) {
                    const { licenseStatus = 'not scanned', key: dmsAppointmentKey, id: dmsAppointmentId, ...dmsAppointment } = selectedAppointment;

                    // mask PII (except if exact match with what customer entered)
                    appointmentDispatch({
                        type: AppointmentDispatchTypes.MaskField,
                        payload: 'all',
                    });
                    if (lookupMethod !== AppointmentLookupMethod.PhoneOrEmail) {
                        if (filter.phone && selectedAppointment.phone) {
                            if (filter.phone === selectedAppointment.phone) {
                                appointmentDispatch({
                                    type: AppointmentDispatchTypes.UnmaskField,
                                    payload: 'phone',
                                });
                            }
                        }

                        if (filter.email && selectedAppointment.email) {
                            if (filter.email.toLowerCase() === selectedAppointment.email.toLowerCase()) {
                                appointmentDispatch({
                                    type: AppointmentDispatchTypes.UnmaskField,
                                    payload: 'email',
                                });
                            }
                        }
                    }
                    else if (!(/[^0-9]/).test(filter.phoneOrEmail)) {
                        if (filter.phoneOrEmail === selectedAppointment.phone) {
                            appointmentDispatch({
                                type: AppointmentDispatchTypes.UnmaskField,
                                payload: 'phone',
                            });
                        }
                    }
                    else if (filter.phoneOrEmail.toLowerCase() === selectedAppointment.email.toLowerCase()) {
                        appointmentDispatch({
                            type: AppointmentDispatchTypes.UnmaskField,
                            payload: 'email',
                        });
                    }

                    /*
                     * The Key is for a DMS appointment, not an internal appointment.
                     * Blank it out to avoid confusion outside this component.
                     */
                    return onAppointmentSelected({
                        ...dmsAppointment,
                        businessName: dmsAppointment.businessName ?? (dmsAppointment.accountType === AccountType.business ? dmsAppointment.lastName : undefined),
                        key: '',
                        id: dmsAppointmentId,
                        appointmentDateTime:
                            getIsAppointmentTimeAfterOffset(dmsAppointment.appointmentDateTime)
                                ? computeNextAvailableCheckinTime()
                                    .toISOString()
                                : dmsAppointment.appointmentDateTime,
                        dmsDealerAppointmentKey: dmsAppointment.dmsAppointmentKey,
                        dmsAppointmentKey,
                        dmsAppointmentId,
                        accountType: dmsAppointment.accountType ?? accountType,
                        licenseStatus,
                        status: 'not scheduled',
                    });
                }
                else if (notListed) {
                    if (newAppointmentEnabled) {
                        const { firstName, lastName, businessName } = filter;
                        const phoneSearch: boolean = !(/[^0-9]/).test(filter.phoneOrEmail);

                        startNotListed({
                            firstName,
                            lastName,
                            businessName: businessName,
                            accountType,
                            phone: lookupMethod === AppointmentLookupMethod.PhoneOrEmail && phoneSearch ? filter.phoneOrEmail : filter.phone,
                            email: lookupMethod === AppointmentLookupMethod.PhoneOrEmail && !phoneSearch ? filter.phoneOrEmail : filter.email,
                        });
                    }
                    else {
                        setSeeServiceAdvisor(true);
                    }
                }
                else if (!loading) {
                    setHasSubmitted(true);

                    const phoneSearch: boolean = !(/[^0-9]/).test(filter.phoneOrEmail);

                    const lookupFilter: AppointmentFilter = {
                        phone: lookupMethod === AppointmentLookupMethod.PhoneOrEmail && phoneSearch ? filter.phoneOrEmail : filter.phone,
                        email: lookupMethod === AppointmentLookupMethod.PhoneOrEmail && !phoneSearch ? filter.phoneOrEmail : filter.email,
                        firstName: filter.firstName,
                        lastName: filter.lastName,
                        businessName: filter.businessName,
                    };

                    setLoading(true);
                    const results: IAppointment[] = await appointmentSearch(key, dealerKey, lookupFilter)
                        .finally(() => setLoading(false));

                    if (lookupMethod === AppointmentLookupMethod.PhoneOrEmail && (!results || results.length === 0) && newAppointmentEnabled) {
                        const phoneSearch: boolean = !(/[^0-9]/).test(filter.phoneOrEmail);

                        startNotListed({
                            firstName: '',
                            lastName: '',
                            businessName: '',
                            accountType: AccountType.individual,
                            phone: phoneSearch ? filter.phoneOrEmail : '',
                            email: !phoneSearch ? filter.phoneOrEmail : '',
                        });
                    }
                    else {
                        return setAppointmentResults(results);
                    }
                }
            }}
            validationSchema={Yup.object()
                .shape({
                    [nameof<typeof initialValues>('email')]: Yup.string()
                        .trim()
                        .email(formatInvalid(Localization.EmailAddress))
                        .nullable(),
                    [nameof<typeof initialValues>('phoneOrEmail')]: Yup.lazy((val) => {
                        return (!(/[^0-9]/).test(val as string)
                            ? Yup.string()
                                .trim()
                                .phone(Localization.EmailOrPhoneInvalid)
                                .nullable()
                            : Yup.string()
                                .trim()
                                .email(Localization.EmailOrPhoneInvalid)
                                .nullable()
                        );
                    }),
                })}
        >
            {({ values, isSubmitting, setFieldValue, submitCount, submitForm, setValues }) => {
                if (submitCount === 0 && initialValues.phoneOrEmail && location.pathname.endsWith('/go')) {
                    navigate(PathName(AppRoutePaths.Appointment, AppointmentRoutePaths.Search, AppointmentLookupMethod.PhoneOrEmail), { replace: true });
                    submitForm();
                }
                let primaryHeading: string = '';
                let icon: IconTypes;
                if (values.lookupMethod) {
                    switch (values.lookupMethod) {
                        case AppointmentLookupMethod.Name:
                            primaryHeading = Localization.LookupName;
                            icon = 'User';
                            break;

                        case AppointmentLookupMethod.Business:
                            primaryHeading = Localization.LookupBusiness;
                            icon = 'Business';
                            break;
                        case AppointmentLookupMethod.PhoneOrEmail:
                            if (dmsType === DMSType.DEALERBUILT) {
                                primaryHeading = Localization.PleaseEnterPhoneNumber;
                            }
                            else {
                                primaryHeading = Localization.PleaseEnterPhoneNumberOrEmail;
                            }
                            icon = 'Profile';
                            break;
                        default:
                            primaryHeading = Localization.LookupPhoneNumber;
                            icon = 'Phone';
                            break;
                    }
                }
                else {
                    switch (values.accountType) {
                        case AccountType.individual:
                            primaryHeading = Localization.WhatIsYourNamePhoneEmail;
                            icon = 'User';
                            break;
                        case AccountType.business:
                            primaryHeading = Localization.LookupBusiness;
                            icon = 'Business';
                            break;
                    }
                }

                /*
                 * TODO: This absolutely should be either an entirely new subcomponent or just placed inline. Also if this is changed
                 * to a component, the phone field completely breaks.
                 */
                const fields: GoMotoFunction<[void], JSX.Element> = () => {
                    if (values.lookupMethod) {
                        switch (values.lookupMethod) {
                            case AppointmentLookupMethod.Name:
                                return (
                                    <>
                                        <GMFormTextField
                                            name={nameof<typeof values>('firstName')}
                                            id={Localization.FirstName}
                                            title={Localization.FirstName}
                                            label={Localization.FirstName}
                                            ariaLabel={Localization.FirstName}
                                            autoFocus
                                            required
                                            onChangeCallback={() => {
                                                setValues({
                                                    ...values,
                                                    selectedAppointment: undefined,
                                                    notListed: false,
                                                });
                                            }}
                                        />
                                        <GMFormTextField
                                            name={nameof<typeof values>('lastName')}
                                            id={Localization.LastName}
                                            title={Localization.LastName}
                                            label={Localization.LastName}
                                            ariaLabel={Localization.LastName}
                                            required
                                            onChangeCallback={() => {
                                                setValues({
                                                    ...values,
                                                    selectedAppointment: undefined,
                                                    notListed: false,
                                                });
                                            }}
                                        />
                                    </>
                                );
                            case AppointmentLookupMethod.Business:
                                return (
                                    <GMFormTextField
                                        name={nameof<typeof values>('businessName')}
                                        id={Localization.Business}
                                        title={Localization.Business}
                                        label={Localization.Business}
                                        ariaLabel={Localization.Business}
                                        autoFocus
                                        required
                                        onChangeCallback={() => {
                                            setValues({
                                                ...values,
                                                selectedAppointment: undefined,
                                                notListed: false,
                                            });
                                        }}
                                    />
                                );
                            case AppointmentLookupMethod.PhoneOrEmail:
                                if (dmsType === DMSType.DEALERBUILT) {
                                    return (
                                        <GMFormPhoneField
                                            name={nameof<typeof values>('phoneOrEmail')}
                                            id={Localization.PhoneNumber}
                                            title={Localization.PhoneNumber}
                                            label={Localization.PhoneNumber}
                                            ariaLabel={Localization.PhoneNumber}
                                            autoFocus
                                            required
                                            onChangeCallback={() => {
                                                setValues({
                                                    ...values,
                                                    selectedAppointment: undefined,
                                                    notListed: false,
                                                });
                                            }}
                                        />
                                    );
                                }
                                else {
                                    return (
                                        <GMFormPhoneOrEmailField
                                            name={nameof<typeof values>('phoneOrEmail')}
                                            id={Localization.PhoneNumberOrEmail}
                                            title={Localization.PhoneNumberOrEmail}
                                            label={Localization.PhoneNumberOrEmail}
                                            ariaLabel={Localization.PhoneNumberOrEmail}
                                            autoFocus
                                            required
                                            onChangeCallback={() => {
                                                setValues({
                                                    ...values,
                                                    selectedAppointment: undefined,
                                                    notListed: false,
                                                });
                                            }}
                                        />
                                    );
                                }
                            default:
                                return (
                                    <GMFormPhoneField
                                        name={nameof<typeof values>('phone')}
                                        id={Localization.PhoneNumber}
                                        title={Localization.PhoneNumber}
                                        label={Localization.PhoneNumber}
                                        ariaLabel={Localization.PhoneNumber}
                                        autoFocus
                                        required
                                        onChangeCallback={() => {
                                            setValues({
                                                ...values,
                                                selectedAppointment: undefined,
                                                notListed: false,
                                            });
                                        }}
                                    />
                                );
                        }
                    }
                    else {
                        return (
                            <>
                                <GMRadioGroup name={nameof<typeof values>('accountType')} ariaLabel={nameof<typeof values>('accountType')}>
                                    <GMRadio value={AccountType.individual} label={Localization.Individual} />
                                    <GMRadio value={AccountType.business} label={Localization.Business} />
                                </GMRadioGroup>
                                {values.accountType === AccountType.business ? (
                                    <GMFormTextField
                                        name={nameof<typeof values>('businessName')}
                                        id={Localization.Business}
                                        title={Localization.Business}
                                        label={Localization.Business}
                                        ariaLabel={Localization.Business}
                                        autoFocus
                                        required
                                        onChangeCallback={() => {
                                            setValues({
                                                ...values,
                                                selectedAppointment: undefined,
                                                notListed: false,
                                            });
                                        }}
                                    />
                                ) : (
                                    <>
                                        <GMFormTextField
                                            name={nameof<typeof values>('firstName')}
                                            id={Localization.FirstName}
                                            title={Localization.FirstName}
                                            label={Localization.FirstName}
                                            ariaLabel={Localization.FirstName}
                                            autoFocus
                                            required
                                            onChangeCallback={() => {
                                                setValues({
                                                    ...values,
                                                    selectedAppointment: undefined,
                                                    notListed: false,
                                                });
                                            }}
                                        />
                                        <GMFormTextField
                                            name={nameof<typeof values>('lastName')}
                                            id={Localization.LastName}
                                            title={Localization.LastName}
                                            label={Localization.LastName}
                                            ariaLabel={Localization.LastName}
                                            required
                                            onChangeCallback={() => {
                                                setValues({
                                                    ...values,
                                                    selectedAppointment: undefined,
                                                    notListed: false,
                                                });
                                            }}
                                        />
                                    </>
                                )}
                                <GMFormPhoneField
                                    name={nameof<typeof values>('phone')}
                                    id={Localization.PhoneNumber}
                                    title={Localization.PhoneNumber}
                                    label={Localization.PhoneNumber}
                                    ariaLabel={Localization.PhoneNumber}
                                    required
                                    onChangeCallback={() => {
                                        setValues({
                                            ...values,
                                            selectedAppointment: undefined,
                                            notListed: false,
                                        });
                                    }}
                                />
                                <GMFormEmailField
                                    name={nameof<typeof values>('email')}
                                    id={Localization.EmailAddress}
                                    title={Localization.EmailAddress}
                                    label={Localization.EmailAddress}
                                    ariaLabel={Localization.EmailAddress}
                                    keyboardLayout={KeyboardLayouts.Email}
                                    required
                                    onChangeCallback={() => {
                                        setValues({
                                            ...values,
                                            selectedAppointment: undefined,
                                            notListed: false,
                                        });
                                    }}
                                />
                            </>
                        );
                    }
                };

                return (
                    <>
                        <GMForm
                            submitButton={
                                <CTAButton
                                    type={'submit'}
                                    styleType={'primary'}
                                    title={values.selectedAppointment || values.notListed ? Localization.ClickToProceedToNextStep : undefined}
                                    label={values.selectedAppointment || values.notListed ? Localization.Proceed : Localization.Search}
                                    onClick={() => keyboardDispatch({
                                        type: KeyboardDispatchTypes.KeyboardHidden,
                                        payload: true,
                                    })}
                                    id={values.selectedAppointment || values.notListed ? Localization.ClickToProceedToNextStep : Localization.Search}
                                    ariaLabel={values.selectedAppointment || values.notListed ? Localization.ClickToProceedToNextStep : Localization.Search}
                                />
                            }
                            heading={
                                <>
                                    <Heading
                                        primaryHeading={AppointmentLookupMethod.PhoneOrEmail === values.lookupMethod && Localization.OkayLetsGetStarted}
                                        secondaryHeading={
                                            <>
                                                {
                                                    mobileCheckInEnabled && !isSubmitting && !hasSubmitted && isKiosk &&
                                                    <>
                                                        <div className={css.buttonWrapper}>
                                                            <GMGenericButton
                                                                onClick={() => navigate(AppRoutePaths.ContinueSession)}
                                                                title={Localization.TapHereToScanCode}
                                                                id={Localization.TapHereToScanCode}
                                                                ariaLabel={Localization.TapHereToScanCode}
                                                            >
                                                                <div className={css.buttonContent}>
                                                                    <div className={css.QRIcon}>
                                                                        <GMSizedIcon iconType={'QR'} iconSize={'large'} />
                                                                    </div>
                                                                    <div className={css.buttonText}>
                                                                        <div>{Localization.HaveAQRCode}</div>
                                                                        <div>{Localization.TapHere}</div>
                                                                    </div>
                                                                </div>
                                                            </GMGenericButton>
                                                        </div>
                                                        <div className={css.orDiv}>
                                                            <GMRoundedIcon roundedBackgroundSize={'small'} themeType={'secondary'}>
                                                                <div className={css.orIcon}>{Localization.Or}</div>
                                                            </GMRoundedIcon>
                                                        </div>
                                                    </>
                                                }
                                                {primaryHeading}
                                            </>
                                        }
                                        roundedIcon={
                                            <GMRoundedIcon roundedBackgroundSize={'large'} themeType={'secondary'}>
                                                <GMSizedIcon iconType={icon} iconSize={'extraLarge'} />
                                            </GMRoundedIcon>
                                        }
                                        showMustache
                                    />
                                </>
                            }
                        >
                            <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                            {fields()}
                            {(isSubmitting || loading) && <Spinner />}
                            {!isSubmitting && !loading && hasSubmitted && !notListedInProgess && !notListedComplete && (
                                <div className={css.appointments}>
                                    <ScrollTo scroll={true}>
                                        <SearchListResults>
                                            {appointments &&
                                                appointments.map<ReactElement<ISearchListResultProps>>((appointment: IAppointment) => (
                                                    <SearchListResult
                                                        key={appointment.key}
                                                        title={getFullDisplayName(appointment)}
                                                        icon={
                                                            <GMSizedIcon
                                                                iconSize={'large'}
                                                                iconType={'User'}
                                                                ariaLabel={Localization.UserIcon}
                                                            />
                                                        }
                                                        checked={appointment.key === values.selectedAppointment?.key}
                                                        onClick={() => {
                                                            if (appointment.key === values.selectedAppointment?.key) {
                                                                setFieldValue(nameof<typeof values>('selectedAppointment'), undefined);
                                                            }
                                                            else {
                                                                setFieldValue(nameof<typeof values>('selectedAppointment'), appointment);
                                                            }
                                                            setFieldValue(nameof<typeof values>('notListed'), false);
                                                        }}
                                                        headerLevel={5}
                                                    >
                                                        <div className={css.datetime}>
                                                            {formatAppointmentDate(appointment, {
                                                                [Locales.en]: 'MMMM Do | hh:mm A',
                                                                [Locales.es]: 'MMMM D | hh:mm A',
                                                                [Locales.fr]: 'D MMMM H [h] mm',
                                                            })}
                                                        </div>
                                                        <GMCapitalize>
                                                            {displayLocalizedVehicle(appointment)}
                                                        </GMCapitalize>
                                                        {appointment.phone?.length > 0 && (
                                                            <div className={css.phoneNumber}>
                                                                {
                                                                    formatAsPhoneNumber(
                                                                        appointment.phone,
                                                                        values.phone !== appointment.phone && values.phoneOrEmail !== appointment.phone,
                                                                    )
                                                                }
                                                            </div>
                                                        )}
                                                    </SearchListResult>
                                                ))}
                                            <SearchListResult
                                                key={'unknown'}
                                                title={Localization.IDoNotSeeMyAppointmentListed}
                                                onClick={() => {
                                                    setValues({
                                                        ...values,
                                                        selectedAppointment: undefined,
                                                        notListed: !values.notListed,
                                                    });
                                                }}
                                                checked={values.notListed}
                                                headerLevel={5}
                                                notListed
                                            />
                                        </SearchListResults>
                                    </ScrollTo>
                                </div>
                            )}
                        </GMForm>
                        <GMDialogError
                            ariaLabel={Localization.PleaseSeeAServiceAdvisorToCheckIn}
                            open={seeServiceAdvisor}
                            onClose={reset}
                        >
                            {Localization.PleaseSeeAServiceAdvisorToCheckIn}
                        </GMDialogError>
                    </>
                );
            }}
        </Formik>
    );
});
